import defaultMapStyle from "./defaultMapStyle.js"
import EventEmitter from "./../utils/EventEmitter"

let _firstInstance = true
let _googleMapLoadedScript = false
const GOOGLE_MAP_LOADED_EVENT_NAME = 'gMapsLoaded'
const GOOGLE_MAP_LOADED_EVENT = new CustomEvent(GOOGLE_MAP_LOADED_EVENT_NAME)

const gMapsCallback = () => {
    _googleMapLoadedScript = true
    window.dispatchEvent(GOOGLE_MAP_LOADED_EVENT)
}

/**
 * Google Map class wrapper
 */
export default class GoogleMap {

    /**
     * @param id String id
     * @param config class configuration
     * @param mapConfig Google Map instance configuration
     */
    constructor(id = "googleMap", config, mapConfig) {

        /**
         * Check if it's the first time the GoogleMap class has been instantiated
         */
        if (_firstInstance) {
            /**
             * Attach a function to the window that will be after the script has loaded
             */
            window.gMapsCallback = gMapsCallback
            _firstInstance = false
        }

        this.id = id

        if (!config) {
            console.error("GoogleMap must have a config object as first parameter");
        }

        this.mapConfig = mapConfig ? mapConfig : {}

        this.isMapReady = false

        this.config = Object.assign({}, {
            icon: false,
            iconWidth: 100,
            iconHeight: 100,
            mapDiv: document.getElementById("map"),
            withCustomStyle: true,
            mapStyle: defaultMapStyle,
            callbackMarkerClick() {
            }
        }, config)

        this.centerMarker = {}
        this.markers = [];
        this.lastMarker = false
        this.currentMarker = false

        this.initEvents();
        this.loadMap();

    }

    initEvents() {

        window.addEventListener(GOOGLE_MAP_LOADED_EVENT_NAME, e => this.initializeMap())

    }

    /**
     * Create the script tag for the google map
     * @private
     */
    loadMap() {

        // if google maps is loaded => Callback.
        if (_googleMapLoadedScript) return gMapsCallback()

        if (!document.getElementById("googleMapScriptTag")) {

            // else add script google maps in head.
            let script_tag = document.createElement('script')
            let key = "AIzaSyDHMWVUC_Bzsop0Bt9YQrGsjsDsitMeDRE"
            let cb = "gMapsCallback"
            script_tag.setAttribute("id", "googleMapScriptTag")
            script_tag.setAttribute("type", "text/javascript")
            script_tag.setAttribute("src", `https://maps.google.com/maps/api/js?key=${key}&callback=${cb}&libraries=geometry&ext=.js`)
            let head = (document.getElementsByTagName("head")[0] || document.documentElement).appendChild(script_tag)

        }

    }

    /**
     * Initialize the map
     * @private
     */
    initializeMap() {
        // if google maps reday, set height on this.
        if (this.isMapReady === false) {
            this.createMap()
        }
    }

    /**
     * Create the google map
     * @private
     */
    createMap() {

        /**
         * Google object
         */
        this.google = google

        this.mapConfig = Object.assign({}, {
            center: new this.google.maps.LatLng(this.config.centerPosition.lat, this.config.centerPosition.lng),
            zoom: 18,
            scrollwheel: false,
            panControl: true,
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: true,
            streetViewControl: false,
            overviewMapControl: false,
            minZoom: 5,
            maxZoom: 30,
            mapTypeControlOptions: { mapTypeIds: ['satellite', 'Normal'] },
        }, this.mapConfig)


        this.mapConfig.styles = this.config.style ? defaultMapStyle : this.config.style

        this.map = new this.google.maps.Map(this.config.mapDiv, this.mapConfig);

        /**
         * Add a custom style to the map
         */
        if (this.config.withCustomStyle) {
            let style = !this.config.style ? defaultMapStyle : this.config.style
            let customStyle = new this.google.maps.StyledMapType(style, { name: 'Normal' })
            this.map.mapTypes.set('Normal', customStyle);
            this.map.setMapTypeId('Normal')
        }

        /**
         * Map ready, can make action on it
         */
        this.isMapReady = true;

        /**
         * Create a a marker for the center position
         */
        if (this.config.makeCenterMarker) {
            this.createCenterMarker();
        }

        /**
         * Create all markers on the map
         */
        this.makeMarkers()

        EventEmitter.emit(`googleMapCreated${this.id}`, {instance: this})

    }

    setMarkers(markers) {
        this.config.markers = markers;
    }

    getMarkers() {
        return this.config.markers;
    }

    setCenterPosition(position) {
        this.config.centerPosition = position;
    }

    makeMarkers() {

        if (this.config.markers && this.config.markers.length > 0) {
            for (var i = 0, len = this.config.markers.length; i < len; i++) {
                var currentMarker = this.config.markers[i];
                if (this.config.onMarkersLoop) {
                    this.config.onMarkersLoop(currentMarker, i)
                } else {
                    this.addMarker(currentMarker.acf.map, currentMarker)
                }
            }
        }
    }

    centerMarkerToMap(marker) {
        if (!marker) {
            marker = this.centerMarker
        }
        google.maps.event.trigger(this.map, "resize")

        if (marker.getPosition) {
            this.map.panTo(marker.getPosition());
        } else {
            //console.log("centermakerposition its not a marker")
        }
    }

    /**
     * Create a marker for the center position of the map
     * @private
     */
    createCenterMarker() {
        const marker = () => {
            const w = this.config.iconWidth
            const h = this.config.iconHeight

            let latLng = new this.google.maps.LatLng(this.config.centerPosition.lat, this.config.centerPosition.lng);
            let markerConfig = {
                position: latLng,
                map: this.map,
                icon: {
                    url: this.config.icon, // url
                    scaledSize: new google.maps.Size(w, h), // scaled size
                    origin: new google.maps.Point(0, 0), // origin
                    anchor: new google.maps.Point(w / 2, h) // anchor
                },
            }
            if (this.config.defaultIcon) {
                markerConfig.icon = null
            }
            this.centerMarker = new this.google.maps.Marker(markerConfig);
            google.maps.event.addListener(this.centerMarker, 'click', () => {
                this.centerMarkerToMap()
            });
        }
        const basicMarkerForPositionRef = () => {
            let latLng = new this.google.maps.LatLng(this.config.centerPosition.lat, this.config.centerPosition.lng);
            let markerConfig = {
                position: latLng,
                map: this.map,
            }
            if (this.config.defaultIcon) {
                markerConfig.icon = null
            }
            this.centerMarker = new this.google.maps.Marker(markerConfig);
            google.maps.event.addListener(this.centerMarker, 'click', () => {
                this.centerMarkerToMap()
            });
        }
        marker();
        //basicMarkerForPositionRef();
    }

    resizeMap() {
        google.maps.event.trigger(this.map, 'resize')
    }

    /**
     * Create a bunch of markers based on an array
     * @param latLng
     * @param markerObject is the data witch define the marker
     */
    addMarker(latLng, markerObject) {

        const that = this

        let marker = new this.google.maps.Marker(Object.assign({
            position: new this.google.maps.LatLng(latLng.lat, latLng.lng),
            map: this.map,
            label: undefined,
            metaData: markerObject,
        }, markerObject))

        this.markers.push(marker)

        google.maps.event.addListener(marker, 'click', function () {
            that.currentMarker = marker
            that.config.callbackMarkerClick(that.currentMarker)
        })

    }

    removeMarkers() {
        for (var i = 0; i < this.markers.length; i++) {
            this.markers[i].setMap(null);
        }
        this.markers.length = 0;
    }


}
